/**
 * Notification Sounds Composable
 *
 * Plays audio alerts for new chat requests and new tickets.
 * Initialized once in MainLayout for authenticated staff users.
 */

import { onUnmounted } from 'vue'
import { useTicketStream } from './useTicketStream'
import { useChatStream } from './useChatStream'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'

const sounds: Record<string, HTMLAudioElement> = {}

function getSound(key: string, src: string): HTMLAudioElement {
  if (!sounds[key]) {
    sounds[key] = new Audio(src)
    sounds[key].volume = 0.5
  }
  return sounds[key]
}

function play(key: string, src: string) {
  try {
    const audio = getSound(key, src)
    audio.currentTime = 0
    audio.play().catch(() => {
      // Browser may block autoplay until user interaction — ignore silently
    })
  } catch {
    // Audio not supported
  }
}

export function useNotificationSounds() {
  const authStore = useAuthStore()

  // Only set up for staff with chat.view permission
  if (!authStore.isStaff) return

  const { onChatQueued } = useChatStream()
  const { onTicketCreated } = useTicketStream()

  const unsubscribeChat = onChatQueued(() => {
    play('chat', '/sounds/chat-sound.wav')
  })

  const notificationStore = useNotificationStore()

  const unsubscribeTicket = onTicketCreated((data) => {
    play('ticket', '/sounds/ticket-sound.mp3')
    const subject = data?.subject || 'New ticket'
    const ticketNumber = data?.ticket_number || ''
    notificationStore.info('New Ticket', ticketNumber ? `${ticketNumber}: ${subject}` : subject)
  })

  // F-010: Clean up subscriptions when the component using this composable unmounts
  onUnmounted(() => {
    unsubscribeChat?.()
    unsubscribeTicket?.()
  })
}
